import { Item } from '../../../app/services/api/generated';

export type SaveStatus = 'standard' | 'idle' | 'saving' | 'saved' | 'error';

export interface UomOption {
    code: string;
    description?: string;
    conversionNumber?: number;
    fromUom?: string;
}

// Dynamic waste reason quantity interface
export interface WasteReasonQuantity {
    reasonId: number;
    reasonName: string;
    quantity?: number | null;
    saveStatus?: SaveStatus;
}

export interface ItemWasteChecksheetData extends Item {
    // Legacy hardcoded fields (for backward compatibility during transition)
    pastDueQty?: number | null;
    sampledQty?: number | null;
    damagedQty?: number | null;
    storeUseQty?: number | null;
    pastDueQtySaveStatus?: SaveStatus;
    sampledQtySaveStatus?: SaveStatus;
    damagedQtySaveStatus?: SaveStatus;
    storeUseQtySaveStatus?: SaveStatus;
    // Per-reason UOM fields (WasteLogUnitSupport)
    pastDueQtyUom?: string;
    sampledQtyUom?: string;
    damagedQtyUom?: string;
    storeUseQtyUom?: string;
    availableUoms?: UomOption[];
    
    // Dynamic waste reasons (new implementation)
    wasteReasonQuantities?: WasteReasonQuantity[];
}

// Configured waste log reason from the API
export interface WasteLogReason {
    id: number;
    name: string;
    description?: string;
    wasteLogSortOrder?: number | null;
}

// Default waste reasons - single source of truth for default names
export const DEFAULT_WASTE_REASONS: WasteLogReason[] = [
  { id: 0, name: 'Past Date' },
  { id: 1, name: 'Sampled' },
  { id: 2, name: 'Damaged' },
  { id: 3, name: 'Store Use' },
];

// Position-based field mappings (single source of truth)
export const POSITION_TO_QTY_FIELD: Record<number, keyof ItemWasteChecksheetData> = {
  0: 'pastDueQty',
  1: 'sampledQty',
  2: 'damagedQty',
  3: 'storeUseQty',
};

export const POSITION_TO_STATUS_FIELD: Record<number, keyof ItemWasteChecksheetData> = {
  0: 'pastDueQtySaveStatus',
  1: 'sampledQtySaveStatus',
  2: 'damagedQtySaveStatus',
  3: 'storeUseQtySaveStatus',
};

export const POSITION_TO_UOM_FIELD: Record<number, keyof ItemWasteChecksheetData> = {
  0: 'pastDueQtyUom',
  1: 'sampledQtyUom',
  2: 'damagedQtyUom',
  3: 'storeUseQtyUom',
};

// Legacy name-based mappings (for backward compatibility with exact name matches)
export const LEGACY_REASON_FIELDS: Record<string, keyof ItemWasteChecksheetData> = Object.fromEntries(
  DEFAULT_WASTE_REASONS.map((reason, index) => [reason.name, POSITION_TO_QTY_FIELD[index]]),
) as Record<string, keyof ItemWasteChecksheetData>;

export const LEGACY_REASON_STATUS_FIELDS: Record<string, keyof ItemWasteChecksheetData> = Object.fromEntries(
  DEFAULT_WASTE_REASONS.map((reason, index) => [reason.name, POSITION_TO_STATUS_FIELD[index]]),
) as Record<string, keyof ItemWasteChecksheetData>;
